import {
  get,
  post,
  postFormData,
  patch,
  put,
  getAPI,
  postAPI,
  deleteWithBody,
  download,
} from "./ApiHelpers.ts";

export function getCVMatchingSetting(slug, interview_type_id) {
  return get(
    `/company/${slug}/interview_type/${interview_type_id}/resume_filters/`
  );
}
export function getPhoneCVMatchingSetting(slug, interview_type_id) {
  return get(
    `/company/${slug}/phone_interview_type/${interview_type_id}/resume_filters/`
  );
}
export function updateCVMatchingSetting(slug, interview_type_id, key, data) {
  return put(
    `/company/${slug}/interview_type/${interview_type_id}/resume_filters/${key}/`,
    data
  );
}
export function updatePhoneCVMatchingSetting(
  slug,
  interview_type_id,
  key,
  data
) {
  return put(
    `/company/${slug}/phone_interview_type/${interview_type_id}/resume_filters/${key}/`,
    data
  );
}
export function updateRoleName(slug: string, interview_type: number, data) {
  return patch(
    `/company/${slug}/interview_type/${interview_type}/update_role/`,
    data
  );
}

export function getAIMatchingSetting(slug, interview_type_id) {
  return get(
    `/company/${slug}/interview_type/${interview_type_id}/ai_matching_settings/`
  );
}
export function getPhoneAIMatchingSetting(slug, interview_type_id) {
  return get(
    `/company/${slug}/phone_interview_type/${interview_type_id}/ai_matching_settings/`
  );
}

export function updateAIMatchingSetting(slug, interview_type_id, key, data) {
  return put(
    `/company/${slug}/interview_type/${interview_type_id}/ai_matching_settings/${key}/`,
    data
  );
}
export function updatePhoneAIMatchingSetting(slug, interview_type_id, key, data) {
  return put(
    `/company/${slug}/phone_interview_type/${interview_type_id}/ai_matching_settings/${key}/`,
    data
  );
}

export function integrateCalendly(slug, data) {
  return post(`/company/${slug}/integrate_calendly/`, data);
}

export function addCalendly(slug, data) {
  return post(`/company/${slug}/add_calendly/`, data);
}

export function getCalendlyConfig(slug, interview_type_id) {
  return get(
    `/company/${slug}/interview_type/${interview_type_id}/get_calendly_integration/`
  );
}

export const getSubscriptionInfoForCompany = (slug) =>
  getAPI(`subscription_details`, slug);

export const getSubscriptionsList = (slug) =>
  getAPI("list_base_subscriptions", slug);

export const postBuyCredit = (slug, payload) =>
  postAPI("add_subscription_to_company", payload, slug);

export const updateInvalidEmail = (slug, payload) =>
  postAPI(`company/${slug}/update_interview_candidate/`, payload);

export const getTeamDetails = (slug) => get(`/company/${slug}/members/`);

export const addTeamDetails = (slug, data) =>
  post(`/company/${slug}/members/`, data);

export const deleteTeamDetails = (slug, data) =>
  deleteWithBody(`/company/${slug}/members/`, data);

export const getKnitAccessToken = (slug) => {
  return get(`/company/${slug}/get_knit_auth_session/`);
};
export const getSubCompanyList = (slug) =>
  get(`/company/${slug}/get_subcompany_list/`);

export const sendReportIssues = (slug, candidateId, payload) =>
  post(`/company/${slug}/candidate/${candidateId}/profile_feedback/`, payload);

export const sendApplicantReportIssue = (slug, roleId, applicantId, payload) =>
  postAPI(`roles/${roleId}/applicants/${applicantId}/report_issue/`, payload, slug);

export const enrichApplicantContact = (slug: string, roleId: string, applicantId: string) =>
  postAPI(`roles/${roleId}/applicant/${applicantId}/enrich-contact/`, {}, slug);

export const shareCandidateProfile = (slug: string, candidateId: number, payload: Record<string, unknown>) =>
  postAPI(`candidate/${candidateId}/share/`, payload, slug);

export const shareApplicantProfile = (
  slug: string,
  roleId: string | number,
  applicantId: number,
  payload: Record<string, unknown>
) => postAPI(`roles/${roleId}/applicants/${applicantId}/share/`, payload, slug);

export const getDownloadAllApplicantList = (slug: string, roleId: string, params: Record<string, string> = {}) => {
  const query = new URLSearchParams(params).toString();
  return download(`/company/${slug}/roles/${roleId}/applicants/download/${query ? `?${query}` : ""}`, "applicants.xlsx");
};

export const getActivityLog = (
  slug: string,
  params?: {
    timestamp_from?: string;
    timestamp_to?: string;
    event_type?: string;
    page?: number;
    page_size?: number;
  }
) => {
  const query = new URLSearchParams(
    params as Record<string, string>
  )?.toString();

  return get(
    `/company/${slug}/candidate_level_activity_logs/${query ? `?${query}` : ""}`
  );
};

export const sendWebKnitInvite = (slug, interviewId, knitId) =>
  post(
    `/company/${slug}/interview_type/${interviewId}/invite_knit_canididates/${knitId}/`,
    {}
  );

export const sendPhoneKnitInvite = (slug, interviewId, knitId) =>
  post(
    `/company/${slug}/phone_interview_type/${interviewId}/invite_knit_canididates/${knitId}/`,
    {}
  );

export const getLatestCuratedList = (slug) =>
  getAPI(`latest_curated_candidates`, slug, { limit: "5" });

export const getApplicantComments = (slug: string, roleId: string, applicantId: string) =>
  getAPI(`roles/${roleId}/applicants/${applicantId}/comment/`, slug);

export const addApplicantComment = (slug: string, roleId: string, applicantId: string, payload: { comment: string; send_to_babblebots: boolean }) =>
  postAPI(`roles/${roleId}/applicants/${applicantId}/comment/`, payload, slug);

export type MyCompany = {
  slug: string;
  name: string;
  logo: string | null;
  // Staff listing only — archived companies are hidden unless requested.
  archived?: boolean;
};

export const getMyCompanies = (
  includeArchived = false
): Promise<{ companies: MyCompany[] }> =>
  get(
    `/company/get_my_companies/${includeArchived ? "?include_archived=1" : ""}`
  );

// Job fair: the shareable team sign-up link (companies add their own
// recruiters at babblebots.ai/ai-talent-drive/join/<token>). Edit admins only.
export const getJoinCampaigns = (slug) => get(`/company/${slug}/join-campaigns/`);

// The response carries the full URL exactly once; the server keeps a hash.
export const createJoinCampaign = (slug, label = "") =>
  post(`/company/${slug}/join-campaigns/`, { label });

export const pauseJoinCampaign = (slug, campaignId) =>
  post(`/company/${slug}/join-campaigns/${campaignId}/pause/`, {});
